'use client';

import React, { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { Loader } from '@/components/common/Loader';
import { dashboardPages } from '@/config/dashboardPages';

interface AuthGuardProps {
  children: React.ReactNode;
}

/** Rutas públicas que no requieren autenticación */
const PUBLIC_ROUTES = ['/login', '/register'];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_ROUTES.some(
    route => pathname === route || pathname.startsWith(route + '/')
  );
}

/**
 * Guard global que protege todas las rutas del dashboard.
 * Las rutas públicas (/login, /register) se renderizan inmediatamente
 * sin esperar a que useAuth termine de verificar.
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname();
  const isPublic = isPublicPath(pathname);

  // Solo llamar useAuth en rutas protegidas para evitar bloquear el login
  const { isAuthenticated, isLoading, userACL, checkPermission, isMenuAllowed } = useAuth();
  const [isChecking, setIsChecking] = useState(!isPublic);

  useEffect(() => {
    // Rutas públicas: no verificar nada
    if (isPublic) {
      setIsChecking(false);
      return;
    }

    if (isLoading) return;

    // Si no está autenticado, redirigir al login
    if (!isAuthenticated) {
      const loginUrl = pathname !== '/'
        ? `/login?redirect=${encodeURIComponent(pathname)}`
        : '/login';
      router.push(loginUrl);
      return;
    }

    // Verificar permisos basados en la ruta actual
    const currentPage = dashboardPages.find(page =>
      pathname === page.url || pathname.startsWith(page.url + '/')
    );

    if (currentPage && currentPage.requiresACL) {
      if (!checkPermission(currentPage.requiresACL)) {
        router.push('/home');
        return;
      }
    }

    // Verificar si el menú está permitido (no en forbidden): menuACL, menuUsuarios, etc.
    if (currentPage && !isMenuAllowed(currentPage.value)) {
      router.push('/home');
      return;
    }

    setIsChecking(false);
  }, [isAuthenticated, isLoading, pathname, userACL, checkPermission, isMenuAllowed, router, isPublic]);

  // Rutas públicas: renderizar inmediatamente
  if (isPublic) {
    return <>{children}</>;
  }

  // Mostrar loader mientras se verifica auth en rutas protegidas
  if (isLoading || isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader />
          <p className="mt-4 text-gray-600">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, no renderizar (ya se redirigió)
  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};
